import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import type { McpIssueHistoryEntry } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

/**
 * What happened to an issue, newest first: each developer's status change and
 * each note of any admin role, in words ("In behandeling genomen", "Opgelost",
 * "Notitie"), who did it (their name, else their email) and when, and the
 * note they left, as they wrote it. Every admin role reads it.
 */
export function IssueHistory({ entries }: { entries: readonly McpIssueHistoryEntry[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) {
    return (
      <p className="text-muted rounded-lg border py-6 text-center text-sm">
        {t("issues.history.empty")}
      </p>
    );
  }
  return (
    <Card>
      <CardContent className="py-2 max-sm:px-4">
        <ol className="divide-border divide-y">
          {entries.map((entry) => {
            const who = entry.actor_name?.trim() || entry.actor_email;
            return (
              <li key={entry.id} data-history={entry.action} className="py-3">
                <p className="text-sm">
                  <span className="text-foreground font-semibold">
                    {t(`issues.history.done.${entry.action}`)}
                  </span>
                  <span className="text-muted">
                    {", "}
                    {who
                      ? t("issues.history.byAt", { who, time: formatDateTime(entry.created_at) })
                      : formatDateTime(entry.created_at)}
                  </span>
                </p>
                {entry.note && (
                  <p className="text-foreground mt-1 text-sm wrap-anywhere whitespace-pre-wrap">
                    {entry.note}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
